import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Divider,
    Paper
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { apiHandler } from '../../../../utils/api/responseHandler';
import { authService as internalAuthService } from '../../../../utils/auth/authService';
import { UserInfo } from '../../../../utils/auth/authTypes';
import type {
    QuoteData,
    WindAverageOption,
    SailUseOption,
    ClothData,
    WeightOption,
    GradeOption,
    GenericOption,
    BattenOptionNew
} from '../../../../types/quoter/types';
import config from '../../../../config';
import logger from '../../../../utils/logging/logger';
import { parseSelectionSummary } from '../../../../utils/formatSelectionSummary';
import PricingDisplay from '../../../shared/PricingDisplay';
import { usePricing } from '../../../../hooks/quoter/usePricing';

const componentName = 'QuoteSummaryPrintModal';

interface QuoteSummaryPrintModalProps {
    open: boolean;
    onClose: () => void;
    quoteId: number;
    showResellerPricing?: boolean; // Set to true for reseller/admin users to show dual pricing buttons
}

interface Boat {
    BoatId: number;
    BoatModel: string;
    I: number | null;
    J: number | null;
    P: number | null;
    E: number | null;
    BoatLengthFt: number;
}

interface ContentBlock {
    ContentHeader: string;
    ContentMain: string;
}

interface ShippingEstimate {
    shippingCost: number;
}

interface SummaryState {
    quoteData: QuoteData | null;
    boat: Boat | null;
    windOption: string;
    useOption: string;
    clothData: ClothData | null;
    weightOverride: string | null;
    gradeOverride: string | null;
    selectedOptions: GenericOption[];
    battens: BattenOptionNew[];
    standardFeatures: string;
    footerContent: string;
    shippingCost: number | null;
    userInfo: UserInfo | null;
    isLoading: boolean;
    error: string | null;
}

const QuoteSummaryPrintModal: React.FC<QuoteSummaryPrintModalProps> = ({
    open,
    onClose,
    quoteId,
    showResellerPricing = false
}) => {
    const [state, setState] = useState<SummaryState>({
        quoteData: null,
        boat: null,
        windOption: '',
        useOption: '',
        clothData: null,
        weightOverride: null,
        gradeOverride: null,
        selectedOptions: [],
        battens: [],
        standardFeatures: '',
        footerContent: '',
        shippingCost: null,
        userInfo: null,
        isLoading: false,
        error: null
    });

    const [currentView, setCurrentView] = useState<'customer' | 'reseller'>('customer');

    // Control reseller pricing display based on prop and current view
    const hideResellerPricing = !showResellerPricing || currentView === 'customer';

    // Use the pricing hook for consistent calculations
    const pricing = usePricing(state.quoteData);

    const formatMeasurement = (value: number | null | undefined): string => {
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        return 'N/A';
    };

    const formatPriceDelta = (priceDelta: number) => {
        if (Math.abs(priceDelta) < 1) return '';
        const sign = priceDelta > 0 ? '+' : '-';
        return ` (${sign}$${Math.abs(Math.round(priceDelta))})`;
    };

    const generateQuoteCode = () => {
        if (!state.userInfo || !state.quoteData) return '';

        const userId = (state.userInfo as any).userId || 0;
        const boatId = state.quoteData.BoatId;
        const paddedQuoteId = quoteId.toString().padStart(5, '0');

        return `${userId}00${boatId}${paddedQuoteId}`;
    };

    const getCurrentDate = () => {
        return new Date().toLocaleDateString('en-US', {
            timeZone: 'America/Los_Angeles',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const fetchSummaryData = useCallback(async () => {
        if (!quoteId || quoteId <= 0 || !open) return;

        setState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            logger.log('LogComponentUpdates', componentName, 'Starting data fetch for quote:', quoteId);

            // Get user info first
            const userInfo = await internalAuthService.ensureUser();

            // Primary fetch - quote data
            const quoteResponse = await apiHandler.fetch<QuoteData>(
                `${config.API_URL}/api/quotes/${quoteId}`
            );

            if (quoteResponse.error) throw new Error('Failed to fetch quote data');
            const quote = quoteResponse.data!;

            // Parallel fetches for all other data
            const [
                boatResponse,
                windResponse,
                useResponse,
                clothResponse,
                weightResponse,
                gradeResponse,
                optionsResponse,
                battensResponse,
                featuresResponse,
                footerResponse
            ] = await Promise.all([
                apiHandler.fetch<Boat>(`${config.API_URL}/api/boat/${quote.BoatId}`),
                apiHandler.fetch<WindAverageOption[]>(`${config.API_URL}/api/quotes/wind-average-answer/${quoteId}`),
                apiHandler.fetch<SailUseOption[]>(`${config.API_URL}/api/quotes/sail-use-answer/${quoteId}`),
                apiHandler.fetch<ClothData>(`${config.API_URL}/api/quotes/${quoteId}/cloth-default`),
                apiHandler.fetch<WeightOption[]>(`${config.API_URL}/api/quotes/${quoteId}/cloth-weight-override`),
                apiHandler.fetch<GradeOption[]>(`${config.API_URL}/api/quotes/${quoteId}/cloth-grade-override`),
                apiHandler.fetch<GenericOption[]>(`${config.API_URL}/api/quotes/${quoteId}/options`),
                [1, 2].includes(quote.SailId) ?
                    apiHandler.fetch<BattenOptionNew[]>(`${config.API_URL}/api/quotes/${quoteId}/battens`) :
                    Promise.resolve({ data: [], error: null }),
                apiHandler.fetch<ContentBlock[]>(
                    `${config.API_URL}/api/content?contentCategoryCode=SAIL_STD_FEATURES&sailId=${quote.SailId}&sailCutId=${quote.SailCutId || 0}&isFurling=${quote.IsFurling || 0}`
                ),
                apiHandler.fetch<ContentBlock[]>(
                    `${config.API_URL}/api/content?contentCategoryCode=QUOTE_PRINTING&contentCode=TIME_LIMIT`,
                    {
                        validateFn: (data) => data,
                    }
                )
            ]);

            // Process responses
            const selectedWind = windResponse.data?.find(opt => opt.IsSelected === 1);
            const selectedUse = useResponse.data?.find(opt => opt.IsSelected === 1);
            const selectedWeight = weightResponse.data?.find(opt => opt.IsSelected);
            const selectedGrade = gradeResponse.data?.find(opt => opt.IsSelected);

            // Debug logging for cloth overrides
            logger.log('LogComponentUpdates', componentName, 'Cloth override data:', {
                weightData: weightResponse.data,
                gradeData: gradeResponse.data,
                selectedWeight,
                selectedGrade
            });
            const selectedOptions = optionsResponse.data || [];
            const standardFeatures = featuresResponse.data?.[0]?.ContentMain || '';
            const footerContent = footerResponse.data?.[0]?.ContentMain || '';

            // Debug logging for footer content
            logger.log('LogComponentUpdates', componentName, 'Footer API response:', {
                footerResponse: footerResponse.data,
                footerContent,
                error: footerResponse.error
            });

            // Get shipping estimate if user has zip code
            let shippingCost: number | null = null;
            if (userInfo && !userInfo.isAnonymous && (userInfo as any).zipCode) {
                try {
                    const shippingResponse = await apiHandler.fetch<ShippingEstimate>(
                        `${config.API_URL}/api/shipping-estimate-quote/${quoteId}`
                    );
                    if (!shippingResponse.error && shippingResponse.data) {
                        shippingCost = shippingResponse.data.shippingCost;
                    }
                } catch (err) {
                    logger.log('LogComponentUpdates', componentName, 'Shipping estimate failed, using fallback');
                }
            }

            setState(prev => ({
                ...prev,
                quoteData: quote,
                boat: boatResponse.data || null,
                windOption: selectedWind?.AnswerText || '',
                useOption: selectedUse?.AnswerText || '',
                clothData: clothResponse.data || null,
                weightOverride: selectedWeight ? `${selectedWeight.WeightDescription}${formatPriceDelta(selectedWeight.PriceDelta)}` : null,
                gradeOverride: selectedGrade ? `${selectedGrade.ClothOverrideDescription}${formatPriceDelta(selectedGrade.PriceDelta)}` : null,
                selectedOptions,
                battens: battensResponse.data || [],
                standardFeatures,
                footerContent,
                shippingCost,
                userInfo,
                isLoading: false,
                error: null
            }));

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load quote summary';
            setState(prev => ({
                ...prev,
                error: errorMessage,
                isLoading: false
            }));
        }
    }, [quoteId, open]);

    useEffect(() => {
        if (open && quoteId > 0) {
            // Reset to customer view when modal opens
            setCurrentView('customer');
            fetchSummaryData();
        }
    }, [open, quoteId, fetchSummaryData]);

    const handlePrint = () => {
        window.print();
    };

    const handlePrintCustomerView = () => {
        setCurrentView('customer');
        // Use setTimeout to ensure state update is applied before printing
        setTimeout(() => {
            window.print();
        }, 100);
    };

    const handlePrintResellerView = () => {
        setCurrentView('reseller');
        // Use setTimeout to ensure state update is applied before printing
        setTimeout(() => {
            window.print();
        }, 100);
    };

    const renderClothConfiguration = () => {
        if (!state.clothData) return null;

        // Check if we actually have meaningful overrides (not just "Recommended")
        const hasWeightOverride = state.weightOverride && !state.weightOverride.toLowerCase().includes('recommended');
        // For grade override, compare the quote's SelectedClothId with the default cloth's OverrideClothId
        // to determine if a different cloth was actually selected
        const hasGradeOverride = state.gradeOverride &&
            !state.gradeOverride.toLowerCase().includes('recommended') &&
            state.quoteData?.SelectedClothId !== state.clothData.OverrideClothId;
        const hasOverrides = hasWeightOverride || hasGradeOverride;

        if (hasOverrides) {
            const defaultCloth = `${state.clothData.Manufacturer} - ${state.clothData.ClothMakeName} ${state.clothData.WeightOz?.toLocaleString()}oz`;
            const overrides = [
                hasWeightOverride ? state.weightOverride : null,
                hasGradeOverride ? state.gradeOverride : null
            ].filter(Boolean);
            return `Upgraded from ${defaultCloth} to ${overrides.join(', ')}`;
        }

        return `${state.clothData.Manufacturer} - ${state.clothData.ClothMakeName} ${state.clothData.WeightOz?.toLocaleString()}oz`;
    };

    const renderSelectedFeatures = () => {
        // Use SelectionSummary from database if available (preferred - single source of truth)
        if (state.quoteData?.SelectionSummary) {
            return parseSelectionSummary(state.quoteData.SelectionSummary).map(item => item.text);
        }

        // Fallback to manual processing if SelectionSummary not available
        const features = [];

        // Add battens for mains
        if (state.battens.length > 0) {
            const selectedBatten = state.battens.find(b => b.IsSelected === 1);
            if (selectedBatten) {
                features.push(`Full Battens: ${selectedBatten.DisplayText}${formatPriceDelta(selectedBatten.CostDelta)}`);
            }
        }

        // Add options with custom filtering rules
        state.selectedOptions.forEach(option => {
            const optionName = option.OptionDetails || '';
            const hasValidRange = option.SelectRangeMin > -1 && option.SelectRangeMax > -1;
            let displayValue = '';

            if (hasValidRange) {
                if (option.IsRangeYesNo === 1) {
                    displayValue = option.ValueSelected === 1 ? 'Yes' : 'No';
                } else {
                    displayValue = option.ValueSelected.toString();
                }
            } else {
                displayValue = option.ValueSelected === 1 ? 'Yes' : 'No';
            }

            const priceDelta = formatPriceDelta(option.OptionQuotedPrice * (option.ValueSelected - option.DefaultValue));

            // Apply custom filtering rules
            
            // Rule: Hide "No" options for specific items
            if (displayValue === 'No' && (
                optionName.includes('Bluewater Construction') ||
                optionName.includes('Overhead Leech Line') ||
                optionName.includes('Cunningham')
            )) {
                return; // Skip this option
            }

            // Rule: Hide "0" value for Sail Numbers
            if (optionName.includes('Sail Numbers') && (displayValue === '0' || option.ValueSelected === 0)) {
                return; // Skip this option
            }

            // Rule: Hide "0" value Leech Profile options
            if (optionName.includes('Leech Profile') && (displayValue === '0' || option.ValueSelected === 0)) {
                return; // Skip this option
            }

            // Rule: For Leech Profile with value 1, show just the profile name without ": 1"
            if (optionName.includes('Leech Profile') && (displayValue === '1' || option.ValueSelected === 1)) {
                features.push(`${optionName}${priceDelta}`);
                return;
            }

            // Default: Show option with value and price delta
            features.push(`${optionName}: ${displayValue}${priceDelta}`);
        });

        return features;
    };

    const renderPricing = () => {
        if (!state.quoteData) return null;

        const hasShipping = state.shippingCost !== null && state.shippingCost > 0;

        return (
            <Box>
                {/* Use the centralized pricing display */}
                <PricingDisplay
                    quoteData={state.quoteData}
                    variant="print"
                    showDisclaimer={false}
                    viewAsCustomer={hideResellerPricing}
                />

                {/* Add shipping section if available */}
                {hasShipping && state.shippingCost !== null && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1">
                                <strong>Shipping:</strong>
                            </Typography>
                            <Typography variant="body1">
                                ${state.shippingCost.toLocaleString()}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1">
                                <strong>Grand Total:</strong>
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                ${((pricing?.retailPrice || 0) + state.shippingCost).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Tax disclaimer */}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {hasShipping ? 'Plus any applicable taxes' : 'Plus shipping and any applicable taxes'}
                </Typography>

                {/* Pricing disclaimer */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        *All prices displayed are estimates and subject to review.
                    </Typography>
                </Box>
            </Box>
        );
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className="print-modal"
            sx={{
                '& .MuiDialog-paper': {
                    width: '90%',
                    maxWidth: '800px',
                    height: '90vh',
                    '@media print': {
                        width: '100%',
                        height: 'auto',
                        maxWidth: 'none',
                        maxHeight: 'none',
                        margin: 0,
                        padding: 0,
                        boxShadow: 'none',
                        overflow: 'visible',
                        fontSize: '0.85rem'
                    }
                },
                // Hide the backdrop and container overflow in print
                '@media print': {
                    '& .MuiBackdrop-root': {
                        display: 'none'
                    },
                    '& .MuiDialog-container': {
                        height: 'auto',
                        overflow: 'visible'
                    }
                }
            }}
        >
            <DialogTitle className="no-print" sx={{ '@media print': { display: 'none' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Quote Summary
                    </Typography>
                    {showResellerPricing && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant={currentView === 'customer' ? 'contained' : 'outlined'}
                                onClick={() => setCurrentView('customer')}
                            >
                                Customer View
                            </Button>
                            <Button
                                size="small"
                                variant={currentView === 'reseller' ? 'contained' : 'outlined'}
                                onClick={() => setCurrentView('reseller')}
                            >
                                Reseller View
                            </Button>
                        </Box>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent
                className="print-content"
                sx={{
                    '@media print': {
                        padding: 0,
                        overflow: 'visible',
                        height: 'auto'
                    }
                }}
            >
                {state.isLoading && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                        <CircularProgress />
                    </Box>
                )}

                {state.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {state.error}
                    </Alert>
                )}

                {!state.isLoading && !state.error && state.quoteData && state.boat && (
                    <Paper
                        elevation={0}
                        className="printable-content"
                        sx={{
                            p: 3,
                            '@media print': {
                                boxShadow: 'none',
                                p: 2,
                                // Override all Typography variants for print, except those with specific overrides
                                '& .MuiTypography-root': {
                                    fontSize: '0.85em'
                                },
                                // Prevent page breaks inside the paper
                                pageBreakInside: 'avoid',
                                // Ensure no extra page after content
                                pageBreakAfter: 'avoid',
                                // Control overflow
                                overflow: 'hidden'
                            }
                        }}
                    >
                        {/* Header with Logo */}
                        <Box sx={{
                            textAlign: 'center',
                            mb: 3,
                            '@media print': {
                                pageBreakInside: 'avoid',
                                pageBreakAfter: 'auto'
                            }
                        }}>
                            <img
                                src="/images/point-sailsLogo.svg"
                                alt="Point Sails"
                                style={{
                                    maxHeight: '80px',
                                    marginBottom: '16px'
                                }}
                            />
                            <Typography 
                                variant="h4" 
                                gutterBottom
                                sx={{
                                    '@media print': {
                                        fontSize: '1.6rem !important',
                                        fontWeight: 'bold !important'
                                    }
                                }}
                            >
                                Sail Quote Request
                                {showResellerPricing && (
                                    <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                                        ({currentView === 'customer' ? 'Customer View' : 'Reseller View'})
                                    </Typography>
                                )}
                            </Typography>
                            <Typography 
                                variant="h5" 
                                gutterBottom
                                sx={{
                                    '@media print': {
                                        fontSize: '1.1rem !important',
                                        fontWeight: 'bold !important'
                                    }
                                }}
                            >
                                {state.boat.BoatModel} - {state.quoteData.SailDisplayName}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {getCurrentDate()}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body1" gutterBottom>
                                <strong>I:</strong> {formatMeasurement(state.boat.I)}, <strong>J:</strong> {formatMeasurement(state.boat.J)},
                                <strong>P:</strong> {formatMeasurement(state.boat.P)}, <strong>E:</strong> {formatMeasurement(state.boat.E)}
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                {/* Only show LP% for headsails (SailId = 3) */}
                                {state.quoteData.SailId === 3 && state.quoteData.PercentLP && (
                                    <span><strong>LP:</strong> {state.quoteData.PercentLP}%, </span>
                                )}
                                <strong>Area:</strong> {state.quoteData.AdjustedSquareFeet?.toFixed(0) || 'N/A'} sq. ft.
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Standard Features - only show in customer view */}
                        {currentView === 'customer' && state.standardFeatures && (
                            <Box sx={{
                                '@media print': {
                                    pageBreakInside: 'avoid'
                                }
                            }}>
                                <Typography variant="h6" gutterBottom>
                                    Standard Features
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                    {state.standardFeatures}
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                            </Box>
                        )}

                        {/* Expected Sailing Conditions - Commented out per request, may need to restore later
                        {(state.windOption || state.useOption) && (
                            <Box sx={{
                                '@media print': {
                                    pageBreakInside: 'avoid'
                                }
                            }}>
                                <Typography variant="h6" gutterBottom>
                                    Expected Sailing Conditions
                                </Typography>
                                {state.windOption && (
                                    <Typography variant="body1">
                                        <strong>Wind Average:</strong> {state.windOption}
                                    </Typography>
                                )}
                                {state.useOption && (
                                    <Typography variant="body1">
                                        <strong>Intended Use:</strong> {state.useOption}
                                    </Typography>
                                )}
                                <Divider sx={{ my: 2 }} />
                            </Box>
                        )}
                        */}

                        {/* Cloth Configuration */}
                        <Box sx={{
                            '@media print': {
                                pageBreakInside: 'avoid'
                            }
                        }}>
                        <Typography variant="h6" gutterBottom>
                            Cloth Configuration
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {renderClothConfiguration()}
                        </Typography>

                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Selected Options */}
                        <Box sx={{
                            '@media print': {
                                pageBreakInside: 'avoid'
                            }
                        }}>
                        <Typography variant="h6" gutterBottom>
                            Selected Options
                        </Typography>
                        <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: 0.5,
                            columnGap: 3
                        }}>
                            {renderSelectedFeatures().map((feature, index) => (
                                <Typography key={index} variant="body1" component="div">
                                    • {feature}
                                </Typography>
                            ))}
                        </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Pricing Summary */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            mt: 3,
                            '@media print': {
                                pageBreakInside: 'avoid',
                                pageBreakBefore: 'auto'
                            }
                        }}>
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Quote Code: {generateQuoteCode()}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Typography variant="h5">
                                            <strong>Total:</strong>
                                        </Typography>
                                        <Typography variant="h5">
                                            <strong>${Math.round(pricing?.retailPrice || 0).toLocaleString()}</strong>
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        + Shipping and Applicable Taxes
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                *All prices displayed are estimates and subject to review.
                            </Typography>
                            {/* Footer Content - Quote validity message */}
                            {state.footerContent && (
                                <Typography sx={{ mt: 0.5 }} variant="body2" color="text.secondary">
                                    {state.footerContent}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                )}
            </DialogContent>

            <DialogActions className="no-print" sx={{ '@media print': { display: 'none' } }}>
                {showResellerPricing ? (
                    // Show dual print buttons for reseller/admin users
                    <>
                        <Button 
                            onClick={handlePrintCustomerView} 
                            startIcon={<PrintIcon />} 
                            variant="outlined"
                            color={currentView === 'customer' ? 'primary' : 'inherit'}
                        >
                            Print Customer View
                        </Button>
                        <Button 
                            onClick={handlePrintResellerView} 
                            startIcon={<PrintIcon />} 
                            variant="outlined"
                            color={currentView === 'reseller' ? 'primary' : 'inherit'}
                        >
                            Print Reseller View
                        </Button>
                    </>
                ) : (
                    // Show single print button for regular users
                    <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined">
                        Print
                    </Button>
                )}
                <Button onClick={onClose} startIcon={<CloseIcon />}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuoteSummaryPrintModal;